import React, { useEffect, useRef, useState } from 'react';
import {
    Heart, ArrowRight, Stethoscope, Home, Camera, ClipboardCheck, Search, Mail,
    Sparkles, PawPrint, FileText, Syringe, Bug, Scissors, BadgeCheck, Clock, Users, Filter, Globe,
} from 'lucide-react';
import { api } from '../../services/api';

/**
 * Portada pública de /albergues (pestaña "Descubrir" de ShelterPortalView).
 *
 * Regla de contenido: SOLO se describe lo que existe hoy en el código (verificado el 2026-10-12):
 *  - Registro de rescate (AddPetForm con rescueStatus="shelter"): foto, especie, raza, nombre,
 *    edad aproximada, sexo, color, señas, ojos y peso. Al registrarlo queda público en /adoptame
 *    (shelter.php → public_adoptable incluye rescue_status 'shelter').
 *  - Ficha del residente (ShelterDashboard): Protocolo de reinserción (6 pasos por defecto,
 *    editables en Configuración) + hitos automáticos, Crecimiento, Carnet, Historial, Galería, Documentos.
 *  - Solicitudes de adopción: nombre, correo, teléfono y tipo de vivienda; se aprueban o rechazan.
 *  - "Vincular / Adoptar": envía un correo a la familia (usuario de Amigo Peludo) para confirmar la adopción.
 * NO se promete: padrinos (no hay forma de hacerse padrino), voluntariado, donaciones ni traspaso de ficha.
 *
 * Las capturas de /public/albergues son del panel y de Adóptame reales, con los datos de
 * demostración que trae el propio panel ("Cargar entorno demo").
 */

const unsplash = (id: string, w = 900, h?: number) =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&q=80&w=${w}${h ? `&h=${h}` : ''}`;

const SHOT = {
    panelDesktop: '/portadas/albergues/panel-escritorio.webp',
    panelMobile: '/portadas/albergues/panel-movil.webp',
    registro: '/portadas/albergues/etapa-1-registro.webp',
    ficha: '/portadas/albergues/etapa-2-ficha.webp',
    adoptame: '/portadas/albergues/etapa-3-adoptame.webp',
    solicitudes: '/portadas/albergues/etapa-4-solicitudes.webp',
};

/** Pasos que el protocolo de reinserción trae por defecto (ShelterDashboard → defaultProtocolOptions). */
const PROTOCOL = [
    { icon: Clock, label: '14 días de cuarentena cumplida' },
    { icon: Stethoscope, label: 'Test de virus y enfermedades infecciosas' },
    { icon: Bug, label: 'Desparasitación interna y externa' },
    { icon: Syringe, label: 'Vacunas base' },
    { icon: Scissors, label: 'Esterilización o castración' },
    { icon: BadgeCheck, label: 'Aprobado para adopción (alta médica)' },
];

type Step = { icon: React.ElementType; title: string; text: string; shot: string; alt: string; device: 'browser' | 'phone'; caption: string };
const JOURNEY: Step[] = [
    {
        icon: Home, title: 'Llega al albergue', device: 'browser', shot: SHOT.registro, alt: 'Formulario "Mascota rescatada" del panel de albergues',
        text: 'Lo registras con su foto, especie, raza, edad aproximada, sexo, color, señas particulares y peso. Desde ese momento tiene su propia ficha.',
        caption: 'Panel · Registrar rescate',
    },
    {
        icon: Stethoscope, title: 'Recupera su salud', device: 'browser', shot: SHOT.ficha, alt: 'Ficha de un residente con su protocolo de reinserción',
        text: 'Su ficha guía el protocolo de reinserción paso a paso y reúne su carnet de vacunas, crecimiento, historial, galería de fotos y documentos.',
        caption: 'Panel · Ficha y protocolo',
    },
    {
        icon: Globe, title: 'Lo conocen en Adóptame', device: 'phone', shot: SHOT.adoptame, alt: 'Página pública Adóptame en un celular',
        text: 'Aparece en Adóptame, la vitrina pública de Amigo Peludo. Cualquiera puede verlo sin crear cuenta, filtrar por especie, raza, color o castración y postular.',
        caption: 'Adóptame · vista en celular',
    },
    {
        icon: Heart, title: 'Elige a su familia', device: 'browser', shot: SHOT.solicitudes, alt: 'Solicitudes de adopción en el panel del albergue',
        text: 'Las solicitudes llegan a tu panel con nombre, correo, teléfono y tipo de vivienda. Apruebas o rechazas, y con “Vincular / Adoptar” la familia recibe un correo para confirmar.',
        caption: 'Panel · Solicitudes de adopción',
    },
];

const VALUES = ['Rescate', 'Rehabilitación', 'Adopción responsable', 'Segundas oportunidades', 'Familias para siempre', 'Amor sin condiciones'];

/* ─────────────────────────── estilos y animaciones ─────────────────────────── */
const HomeStyles: React.FC = () => (
    <>
        {/* Serif cálida para los acentos (React 19 la sube al <head>) */}
        <link rel="stylesheet" precedence="default"
            href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,650;1,9..144,500;1,9..144,600&display=swap" />
        <style>{`
            .sh-serif { font-family: 'Fraunces', Georgia, serif; }
            .sh-grad-text { background: linear-gradient(100deg, #ff6b3d, #f43f74 55%, #c2417f); -webkit-background-clip: text; background-clip: text; color: transparent; }
            @keyframes shFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-12px) rotate(var(--r,0deg)); } }
            @keyframes shBlob { 0%,100% { border-radius: 42% 58% 63% 37% / 41% 44% 56% 59%; transform: translate(0,0) scale(1); } 50% { border-radius: 58% 42% 38% 62% / 55% 61% 39% 45%; transform: translate(3%, -4%) scale(1.08); } }
            @keyframes shPaw { 0% { transform: translateY(0) rotate(var(--r,0deg)); opacity: 0; } 15% { opacity: .55; } 100% { transform: translateY(-260px) rotate(calc(var(--r,0deg) + 25deg)); opacity: 0; } }
            @keyframes shBeat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.18); } 28% { transform: scale(1); } 42% { transform: scale(1.12); } 70% { transform: scale(1); } }
            @keyframes shMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
            @keyframes shPop { 0% { opacity: 0; transform: translateY(14px) scale(.96); } 100% { opacity: 1; transform: none; } }
            @keyframes shShine { 0% { transform: translateX(-120%) skewX(-18deg); } 60%,100% { transform: translateX(260%) skewX(-18deg); } }
            .sh-float { animation: shFloat 6s ease-in-out infinite; }
            .sh-blob { animation: shBlob 14s ease-in-out infinite; }
            .sh-beat { animation: shBeat 1.6s ease-in-out infinite; }
            .sh-marquee { animation: shMarquee 32s linear infinite; }
            .sh-pop { animation: shPop .8s cubic-bezier(.2,.8,.2,1) both; }
            .sh-reveal { opacity: 0; transform: translateY(28px); transition: opacity .9s cubic-bezier(.2,.8,.2,1), transform .9s cubic-bezier(.2,.8,.2,1); }
            .sh-reveal.is-in { opacity: 1; transform: none; }
            .sh-line { transform-origin: top; transform: scaleY(0); transition: transform 1.8s cubic-bezier(.4,0,.2,1); }
            .is-in .sh-line, .sh-line.is-in { transform: scaleY(1); }
            .sh-check { opacity: .35; transition: opacity .5s, transform .5s; }
            .sh-check.done { opacity: 1; }
            .sh-check .sh-tick { transform: scale(0); transition: transform .45s cubic-bezier(.3,1.6,.5,1); }
            .sh-check.done .sh-tick { transform: scale(1); }
            @media (prefers-reduced-motion: reduce) {
                .sh-float, .sh-blob, .sh-beat, .sh-marquee, .sh-pop, .sh-paw { animation: none !important; }
                .sh-reveal, .sh-line { opacity: 1; transform: none; transition: none; }
            }
        `}</style>
    </>
);

/** Activa la clase is-in cuando el elemento entra en pantalla (una sola vez). */
const useInView = <T extends HTMLElement>(threshold = 0.2) => {
    const ref = useRef<T>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || inView) return;
        if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold });
        io.observe(el);
        return () => io.disconnect();
    }, [inView, threshold]);
    return [ref, inView] as const;
};

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({ children, delay = 0, className = '' }) => {
    const [ref, inView] = useInView<HTMLDivElement>(0.15);
    return <div ref={ref} className={`sh-reveal ${inView ? 'is-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = 'text-rose-500' }) => (
    <p className={`text-[11px] font-bold uppercase tracking-[0.28em] mb-3 ${className}`}>{children}</p>
);

/** Marco de navegador para capturas de escritorio. */
const BrowserFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-2xl overflow-hidden bg-white ring-1 ring-rose-100 shadow-[0_30px_60px_-28px_rgba(120,40,60,.5)] ${className}`}>
        <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#fbf3f4] border-b border-rose-100">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff8a7a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#ffd27a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9be0a6]" />
            <span className="ml-3 flex-1 truncate rounded-full bg-white px-3 py-0.5 text-[10px] text-[#9a7d86] ring-1 ring-rose-100">amigopeludo.cl/albergues</span>
        </div>
        <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
    </div>
);

/** Marco de celular para capturas móviles. */
const PhoneFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-[2.4rem] bg-[#2a1d24] p-2 shadow-[0_30px_60px_-24px_rgba(60,20,40,.6)] ${className}`}>
        <div className="relative rounded-[2rem] overflow-hidden bg-white">
            <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 w-16 h-4 rounded-full bg-[#2a1d24]" />
            <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
        </div>
    </div>
);

/** Huellitas que suben suavemente en el fondo. */
const PawField: React.FC = () => (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
            { l: '8%', b: '6%', s: 22, r: '-20deg', d: '9s', dl: '0s' }, { l: '22%', b: '0%', s: 16, r: '15deg', d: '11s', dl: '3s' },
            { l: '47%', b: '4%', s: 20, r: '-8deg', d: '10s', dl: '6s' }, { l: '68%', b: '2%', s: 14, r: '22deg', d: '12s', dl: '1.5s' },
            { l: '86%', b: '8%', s: 24, r: '-14deg', d: '9.5s', dl: '4.5s' },
        ].map((p, i) => (
            <PawPrint key={i} className="sh-paw absolute text-rose-300/70"
                style={{ left: p.l, bottom: p.b, width: p.s, height: p.s, ['--r' as any]: p.r, animation: `shPaw ${p.d} ease-in ${p.dl} infinite` }} />
        ))}
    </div>
);

/* ─────────────────────────── secciones ─────────────────────────── */
const Hero: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#fff7ef] via-[#ffece4] to-[#fde4ec]">
        <div aria-hidden className="sh-blob absolute -top-32 -left-24 w-[34rem] h-[34rem] bg-gradient-to-br from-amber-200/60 to-orange-200/40 blur-3xl" />
        <div aria-hidden className="sh-blob absolute -bottom-40 right-[-10rem] w-[38rem] h-[38rem] bg-gradient-to-tr from-rose-300/50 to-fuchsia-200/40 blur-3xl" style={{ animationDelay: '-6s' }} />
        <PawField />

        <div className="relative max-w-7xl ml-auto mr-auto px-6 pt-28 md:pt-36 pb-20 md:pb-28 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div className="text-center lg:text-left">
                <span className="sh-pop inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-rose-100 text-rose-600 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] shadow-sm">
                    <Heart size={13} className="sh-beat fill-rose-500 text-rose-500" /> Para albergues y fundaciones
                </span>
                <h1 className="sh-pop sh-serif mt-6 text-[2.6rem] sm:text-6xl lg:text-[4.2rem] leading-[1.03] font-semibold text-[#3b2430] tracking-[-0.02em]" style={{ animationDelay: '.1s' }}>
                    Cada rescate merece un <em className="sh-grad-text italic">final feliz</em>
                </h1>
                <p className="sh-pop mt-6 text-lg text-[#7a5d66] leading-relaxed max-w-xl ml-auto mr-auto lg:ml-0 lg:mr-0" style={{ animationDelay: '.2s' }}>
                    Tú pones el amor; nosotros ordenamos todo lo demás. La ficha de cada rescatado, su protocolo sanitario, su carnet y las solicitudes de adopción en un solo panel, en el computador o en el celular.
                </p>
                <div className="sh-pop mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start" style={{ animationDelay: '.3s' }}>
                    <button onClick={onRegister}
                        className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#f43f74] text-white font-bold text-sm tracking-wide shadow-[0_18px_40px_-14px_rgba(244,63,116,.7)] hover:shadow-[0_22px_48px_-12px_rgba(244,63,116,.8)] hover:-translate-y-0.5 transition-all">
                        <span aria-hidden className="absolute inset-y-0 left-0 w-1/3 bg-white/30" style={{ animation: 'shShine 3.2s ease-in-out infinite' }} />
                        Registrar mi albergue <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button onClick={onLogin}
                        className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/80 backdrop-blur border border-rose-100 text-[#5b3a46] font-bold text-sm tracking-wide hover:bg-white transition-all">
                        Ya tengo cuenta
                    </button>
                </div>
                <a href="/adoptame" className="sh-pop mt-6 inline-flex items-center gap-2 text-sm font-semibold text-rose-600 hover:gap-3 transition-all" style={{ animationDelay: '.4s' }}>
                    <PawPrint size={16} /> ¿Quieres adoptar? Conoce a quienes esperan un hogar <ArrowRight size={15} />
                </a>
            </div>

            {/* El panel real: escritorio + celular */}
            <div className="relative ml-auto mr-auto w-full max-w-[38rem] pb-10 sm:pb-16">
                <div className="sh-pop relative" style={{ animationDelay: '.15s' }}>
                    <BrowserFrame src={SHOT.panelDesktop} alt="Panel de control del albergue en el computador" />
                </div>
                <div className="sh-float absolute -right-2 sm:-right-6 -bottom-2 w-[34%] max-w-[11.5rem]" style={{ ['--r' as any]: '3deg' }}>
                    <PhoneFrame src={SHOT.panelMobile} alt="Panel del albergue en el celular" />
                </div>
                <div className="sh-float absolute -left-3 sm:-left-8 -bottom-4 w-28 sm:w-36 aspect-square rounded-[1.75rem] overflow-hidden ring-[6px] ring-white shadow-xl" style={{ animationDelay: '-3s', ['--r' as any]: '-4deg' }}>
                    <img src={unsplash('1450778869180-41d0601e046e', 300, 300)} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="absolute -bottom-6 left-0 right-0 text-center text-[10px] text-[#b09199] italic">Capturas reales del panel, con sus datos de demostración</p>
            </div>
        </div>
        <svg aria-hidden viewBox="0 0 1440 90" preserveAspectRatio="none" className="relative block w-full h-12 md:h-20 text-white" fill="currentColor">
            <path d="M0 90V45C160 10 330 0 520 18c190 18 300 62 500 58 170-3 290-40 420-58v72H0Z" />
        </svg>
    </section>
);

const ValuesMarquee: React.FC = () => (
    <div className="bg-white py-6 overflow-hidden border-b border-rose-50">
        <div className="sh-marquee flex w-max gap-10 whitespace-nowrap">
            {[...VALUES, ...VALUES].map((v, i) => (
                <span key={i} className="sh-serif italic text-2xl md:text-3xl text-[#d9b8c0] flex items-center gap-10">
                    {v} <PawPrint size={18} className="text-rose-300 not-italic" />
                </span>
            ))}
        </div>
    </div>
);

/** El recorrido, con la pantalla real que corresponde a cada etapa. */
const Journey: React.FC = () => {
    const [ref, inView] = useInView<HTMLDivElement>(0.08);
    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-2xl ml-auto mr-auto mb-16">
                    <Eyebrow>El camino de un rescatado</Eyebrow>
                    <h2 className="sh-serif text-4xl md:text-5xl font-semibold text-[#3b2430] tracking-[-0.02em] leading-tight">De la calle a un <em className="sh-grad-text italic">hogar para siempre</em></h2>
                    <p className="mt-4 text-[#7a5d66] leading-relaxed">Así acompaña el panel cada etapa. Las imágenes son las pantallas reales que usa tu equipo.</p>
                </Reveal>

                <div ref={ref} className={`relative ${inView ? 'is-in' : ''}`}>
                    <div aria-hidden className="sh-line absolute left-6 md:left-1/2 top-2 bottom-2 w-[3px] md:-translate-x-1/2 rounded-full bg-gradient-to-b from-amber-300 via-rose-400 to-fuchsia-400" />
                    <div className="space-y-14 md:space-y-24">
                        {JOURNEY.map((s, i) => (
                            <Reveal key={s.title} delay={i * 100} className="relative grid md:grid-cols-2 gap-6 md:gap-16 items-center pl-16 md:pl-0">
                                <span className="absolute left-6 md:left-1/2 top-6 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 w-12 h-12 rounded-full bg-white border-4 border-rose-100 shadow-lg flex items-center justify-center z-10">
                                    <s.icon size={20} className="text-rose-500" />
                                </span>
                                <div className={`${i % 2 ? 'md:order-2 md:pl-12' : 'md:text-right md:pr-12'}`}>
                                    <p className="sh-serif text-rose-400 text-lg italic">Etapa {i + 1}</p>
                                    <h3 className="sh-serif text-2xl md:text-3xl font-semibold text-[#3b2430] mt-1">{s.title}</h3>
                                    <p className="text-[#7a5d66] leading-relaxed mt-3 md:max-w-sm md:inline-block">{s.text}</p>
                                </div>
                                <figure className={`${i % 2 ? 'md:order-1 md:pr-12' : 'md:pl-12'}`}>
                                    {s.device === 'phone'
                                        ? <PhoneFrame src={s.shot} alt={s.alt} className="w-[62%] max-w-[15rem] ml-auto mr-auto -rotate-2" />
                                        : <BrowserFrame src={s.shot} alt={s.alt} />}
                                    <figcaption className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#b09199]">{s.caption}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

/** El protocolo que trae el panel por defecto, completándose solo. */
const ProtocolSection: React.FC = () => {
    const [ref, inView] = useInView<HTMLDivElement>(0.3);
    const [done, setDone] = useState(0);
    useEffect(() => {
        if (!inView) return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setDone(PROTOCOL.length); return; }
        let n = 0;
        const t = setInterval(() => { n += 1; setDone(n); if (n >= PROTOCOL.length) clearInterval(t); }, 650);
        return () => clearInterval(t);
    }, [inView]);
    const pct = Math.round((done / PROTOCOL.length) * 100);

    return (
        <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#fff8f3] to-[#fdeef2]">
            <div aria-hidden className="sh-blob absolute top-10 -left-32 w-[26rem] h-[26rem] bg-emerald-100/60 blur-3xl" />
            <div className="relative max-w-6xl ml-auto mr-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <Eyebrow className="text-emerald-600">Protocolo de reinserción</Eyebrow>
                    <h2 className="sh-serif text-4xl md:text-5xl font-semibold text-[#3b2430] tracking-[-0.02em] leading-tight">Sano, cuidado y <em className="italic text-emerald-600">listo para amar</em></h2>
                    <p className="mt-5 text-[#7a5d66] leading-relaxed">Cada residente tiene su protocolo con un porcentaje de avance. Trae estos 6 pasos por defecto y puedes agregar los propios de tu albergue. Además, algunos hitos se marcan solos cuando registras su edad, su peso, sus fotos, su microchip o sus documentos.</p>
                    <ul className="mt-7 space-y-3 text-sm text-[#5b3a46]">
                        {([
                            [ClipboardCheck, 'Carnet de vacunas, crecimiento (peso y talla) e historial de consultas'],
                            [Camera, 'Galería con las fotos del rescate y de su evolución'],
                            [FileText, 'Documentos como certificados médicos o de microchip'],
                        ] as [React.ElementType, string][]).map(([Icon, t]) => (
                            <li key={t} className="flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0"><Icon size={14} className="text-emerald-600" /></span><span className="pt-1">{t}</span></li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal delay={150}>
                    <div ref={ref} className="relative bg-white/90 backdrop-blur rounded-[2.25rem] p-6 md:p-8 shadow-[0_40px_80px_-35px_rgba(60,90,80,.45)] border border-white">
                        <div className="flex items-center gap-4 pb-5 border-b border-rose-50">
                            <span className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center"><ClipboardCheck size={24} className="text-emerald-600" /></span>
                            <div className="flex-1">
                                <p className="sh-serif text-xl font-semibold text-[#3b2430]">Protocolo de reinserción</p>
                                <p className="text-xs text-[#9a7d86]">Los 6 pasos que trae por defecto</p>
                            </div>
                            <div className="text-right">
                                <p className="sh-serif text-3xl font-semibold text-emerald-600 leading-none">{pct}%</p>
                                <p className="text-[10px] uppercase tracking-widest text-[#9a7d86]">avance</p>
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-emerald-50 mt-5 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
                        </div>
                        <ul className="mt-5 space-y-2.5">
                            {PROTOCOL.map((p, i) => (
                                <li key={p.label} className={`sh-check flex items-center gap-3 rounded-2xl px-3 py-2.5 ${i < done ? 'done bg-emerald-50/70' : 'bg-slate-50/60'}`}>
                                    <span className="relative w-7 h-7 rounded-full border-2 border-emerald-200 flex items-center justify-center shrink-0">
                                        <span className="sh-tick absolute inset-0 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">✓</span>
                                    </span>
                                    <p.icon size={16} className="text-emerald-600 shrink-0" />
                                    <span className="text-sm font-medium text-[#3b2430]">{p.label}</span>
                                </li>
                            ))}
                        </ul>
                        {done >= PROTOCOL.length && (
                            <div className="sh-pop mt-5 rounded-2xl bg-gradient-to-r from-rose-500 to-orange-400 text-white px-4 py-3 flex items-center gap-2 text-sm font-semibold">
                                <Sparkles size={16} /> Alta médica: listo para encontrar familia
                            </div>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

/** Animales reales publicados por albergues en Adóptame. Si aún no hay, se dice tal cual. */
const WaitingForHome: React.FC<{ onRegister: () => void }> = ({ onRegister }) => {
    const [pets, setPets] = useState<any[] | null>(null);
    useEffect(() => {
        let alive = true;
        api.shelter.getPublicAdoptable({ limit: 48 }).then((res: any) => {
            if (!alive) return;
            const list = res?.success && Array.isArray(res.pets) ? res.pets : [];
            // Solo animales de albergues (no avisos de dueños particulares) y con foto.
            setPets(list.filter((p: any) => !p.is_individual && p.image_url).slice(0, 4));
        }).catch(() => alive && setPets([]));
        return () => { alive = false; };
    }, []);

    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-2xl ml-auto mr-auto mb-12">
                    <Eyebrow>En Adóptame ahora</Eyebrow>
                    <h2 className="sh-serif text-4xl md:text-5xl font-semibold text-[#3b2430] tracking-[-0.02em] leading-tight">Esperan un <em className="sh-grad-text italic">hogar</em></h2>
                </Reveal>

                {pets === null ? (
                    <div className="flex justify-center py-10"><Heart className="sh-beat text-rose-300" size={34} /></div>
                ) : pets.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            {pets.map((p, i) => (
                                <Reveal key={p.id} delay={i * 90}>
                                    <a href="/adoptame" className="group block rounded-[1.75rem] overflow-hidden bg-[#fff8f5] ring-1 ring-rose-100 shadow-[0_20px_40px_-26px_rgba(120,40,60,.5)] hover:-translate-y-1.5 transition-transform">
                                        <div className="aspect-[4/5] overflow-hidden"><img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                                        <div className="p-4">
                                            <p className="sh-serif text-xl font-semibold text-[#3b2430] truncate">{p.name}</p>
                                            <p className="text-xs text-[#9a7d86] truncate">{[p.breed, p.shelter_name].filter(Boolean).join(' · ')}</p>
                                        </div>
                                    </a>
                                </Reveal>
                            ))}
                        </div>
                        <div className="text-center mt-10"><a href="/adoptame" className="inline-flex items-center gap-2 font-semibold text-rose-600 hover:gap-3 transition-all">Ver todos en Adóptame <ArrowRight size={16} /></a></div>
                    </>
                ) : (
                    <Reveal className="max-w-2xl ml-auto mr-auto text-center rounded-[2rem] bg-gradient-to-br from-[#fff4ee] to-[#fdeaf0] ring-1 ring-rose-100 px-8 py-12">
                        <div className="ml-auto mr-auto w-14 h-14 rounded-full bg-white shadow flex items-center justify-center mb-5"><PawPrint size={24} className="text-rose-500" /></div>
                        <p className="sh-serif text-2xl md:text-3xl font-semibold text-[#3b2430] leading-snug">Todavía no hay albergues publicando en Adóptame</p>
                        <p className="text-[#7a5d66] leading-relaxed mt-3">El tuyo puede ser el primero: apenas registres a tus rescatados, sus fichas aparecerán aquí y en Adóptame.</p>
                        <button onClick={onRegister} className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#f43f74] text-white font-bold text-sm shadow-[0_14px_30px_-12px_rgba(244,63,116,.7)] hover:-translate-y-0.5 transition-all">
                            Registrar mi albergue <ArrowRight size={16} />
                        </button>
                    </Reveal>
                )}
            </div>
        </section>
    );
};

/** Adóptame: la vitrina pública donde las familias conocen a los rescatados. */
const AdoptameShowcase: React.FC = () => (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-[#3b2430] via-[#5a2b44] to-[#7a2f52] text-white">
        <div aria-hidden className="sh-blob absolute -top-24 right-[-6rem] w-[30rem] h-[30rem] bg-rose-400/25 blur-3xl" />
        <div className="relative max-w-6xl ml-auto mr-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
            <Reveal className="relative ml-auto mr-auto w-full max-w-[16rem]">
                <PhoneFrame src={SHOT.adoptame} alt="Adóptame en el celular" className="sh-float" />
                {[0, 1].map(i => (
                    <span key={i} className="sh-float absolute w-12 h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center"
                        style={{ top: ['10%', '70%'][i], left: ['-14%', '96%'][i], animationDelay: `${-i * 1.7}s` }}>
                        <Heart size={20} className="text-rose-500 fill-rose-500 sh-beat" style={{ animationDelay: `${i * 0.3}s` }} />
                    </span>
                ))}
            </Reveal>
            <Reveal delay={120}>
                <Eyebrow className="text-amber-300">Adóptame</Eyebrow>
                <h2 className="sh-serif text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-tight">Una vitrina para <em className="italic text-rose-200">cada historia</em></h2>
                <p className="mt-5 text-white/75 leading-relaxed">Adóptame es la página pública de Amigo Peludo donde tus rescatados se encuentran con quienes buscan un compañero. Es gratuita para quien adopta y no pide crear cuenta para mirar.</p>
                <ul className="mt-7 grid sm:grid-cols-2 gap-3 text-sm">
                    {([
                        [Filter, 'Filtros por especie, raza, color y castración'],
                        [Search, 'Ficha con foto, edad y personalidad'],
                        [Mail, 'Postulación con datos de contacto y vivienda'],
                        [Users, 'Albergues y dueños particulares en un solo lugar'],
                    ] as [React.ElementType, string][]).map(([Icon, t]) => (
                        <li key={t} className="flex items-start gap-2.5 bg-white/5 rounded-2xl px-4 py-3 border border-white/10"><Icon size={16} className="text-rose-200 shrink-0 mt-0.5" /><span className="text-white/85">{t}</span></li>
                    ))}
                </ul>
                <blockquote className="mt-8 border-l-2 border-rose-300/60 pl-5 sh-serif italic text-xl text-white/90 leading-relaxed">
                    No podemos salvar a todos, pero para cada uno de ellos, ser salvado lo es todo.
                </blockquote>
            </Reveal>
        </div>
    </section>
);

const FinalCta: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="bg-white py-20 md:py-28">
        <div className="max-w-5xl ml-auto mr-auto px-6">
            <Reveal>
                <div className="relative overflow-hidden rounded-[2.75rem] bg-gradient-to-br from-[#fff1e8] via-[#ffe3ea] to-[#f7dcf0] px-8 py-14 md:p-16 text-center shadow-[0_40px_90px_-45px_rgba(160,50,90,.6)]">
                    <div aria-hidden className="sh-blob absolute -top-20 -left-16 w-72 h-72 bg-white/60 blur-2xl" />
                    <PawField />
                    <div className="relative">
                        <div className="ml-auto mr-auto w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center mb-6">
                            <Heart size={28} className="text-rose-500 fill-rose-500 sh-beat" />
                        </div>
                        <h2 className="sh-serif text-4xl md:text-5xl font-semibold text-[#3b2430] tracking-[-0.02em] leading-tight">Suma tu albergue a <em className="sh-grad-text italic">Amigo Peludo</em></h2>
                        <p className="mt-4 text-[#7a5d66] leading-relaxed max-w-xl ml-auto mr-auto">Menos planillas y papeles; más tiempo para lo que importa: cuidarlos y encontrarles una familia.</p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                            <button onClick={onRegister} className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#f43f74] text-white font-bold text-sm shadow-[0_18px_40px_-14px_rgba(244,63,116,.7)] hover:-translate-y-0.5 transition-all">
                                Registrar mi albergue <ArrowRight size={17} />
                            </button>
                            <button onClick={onLogin} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-[#5b3a46] font-bold text-sm border border-rose-100 hover:bg-rose-50 transition-all">
                                Ingresar al panel
                            </button>
                        </div>
                        <p className="mt-6 text-xs text-[#9a7d86] flex items-center justify-center gap-1.5"><FileText size={13} /> Tu albergue usa la misma cuenta de Amigo Peludo: si ya tienes una, solo solicitas el acceso.</p>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

export const ShelterHome: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <div className="font-sans text-[#3b2430] overflow-x-hidden">
        <HomeStyles />
        <Hero onRegister={onRegister} onLogin={onLogin} />
        <ValuesMarquee />
        <Journey />
        <ProtocolSection />
        <WaitingForHome onRegister={onRegister} />
        <AdoptameShowcase />
        <FinalCta onRegister={onRegister} onLogin={onLogin} />
    </div>
);

export default ShelterHome;
